import {
  Authorizer,
  AuthenticationError,
  AuthErrorCodes,
  CookieAuthServiceAuthorizationProvider,
  OlympusAuthorizationProvider,
} from '@owner/auth'
import type { OlympusUser, OlympusBrandSession } from '@owner/auth'
import { shallowRef, type Ref } from 'vue'

import { logError, logInfo } from '@/logger/logger'
import { useEmitter } from '@/utils/useEmitter'

import type { BrandSession, User } from './types'

const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL

let authorizerInstance: Authorizer | null = null
// Deduplicates concurrent callers: the first caller stores its promise here so
// later callers await it instead of starting a second initialize().
let initializingPromise: Promise<void> | null = null
// Bumped by resetAuthorizer() so an in-flight initialize() that started before
// the reset can detect staleness and discard its result instead of overwriting
// the cleared state.
let initGeneration = 0

/**
 * Reactive handle on the Authorizer. Flips from `null` to the instance after
 * `initializeAuthorizer` resolves, and back to `null` on `resetAuthorizer`.
 * Read this in computeds (e.g. the sidebar nav filter) so they re-evaluate
 * once the Authorizer is ready, instead of caching a `null` result.
 */
const authorizerRef: Ref<Authorizer | null> = shallowRef(null)

export const useAuthorizerRef = (): Ref<Authorizer | null> => authorizerRef

/**
 * Get the Authorizer singleton instance
 * Components use this to access authorizer.can(), authorizer.hasRole(), etc. directly
 */
export const useAuthorizer = (): Authorizer => {
  if (!authorizerInstance) {
    throw new Error(
      'Authorizer not initialized. Call initializeAuthorizer() after login.'
    )
  }
  return authorizerInstance
}

const initializeAuthorizer = async (
  user: OlympusUser,
  brandSession: OlympusBrandSession,
  sessionProvider?: 'olympus' | 'auth-service',
  getCsrfToken?: () => string | null | undefined
): Promise<void> => {
  const generation = initGeneration

  const provider =
    sessionProvider === 'auth-service'
      ? new CookieAuthServiceAuthorizationProvider({
          authServiceUrl: AUTH_SERVICE_URL,
          // Wildcard for internal users with no brand context; auth-service
          // gates wildcard permissions on isInternalUser server-side.
          brandId: brandSession.brandId ?? '*',
          // Read the CSRF token lazily on each request so the latest
          // value (set after login or refresh) is always used. Caller
          // owns the storage so this package stays decoupled from the
          // Hermes auth store.
          getCsrfToken: getCsrfToken ?? ((): null => null),
        })
      : new OlympusAuthorizationProvider(user, brandSession)

  const authorizer = new Authorizer({
    // No background refresh needed - Hermes re-initializes on login/brand switch
    autoRefresh: false,
    provider,
  })
  await authorizer.initialize()

  if (generation !== initGeneration) {
    authorizer.destroy()
    return
  }

  authorizerInstance = authorizer
  authorizerRef.value = authorizer
}

export const resetAuthorizer = (): void => {
  if (authorizerInstance) {
    authorizerInstance.destroy()
  }
  authorizerInstance = null
  authorizerRef.value = null
  initializingPromise = null
  initGeneration++
}

/**
 * Get the Authorizer instance if initialized, or null otherwise
 * Use this for conditional checks where you need TypeScript type narrowing
 */
export const getAuthorizer = (): Authorizer | null => {
  return authorizerInstance
}

export const waitForAuthorizer = async (): Promise<Authorizer | null> => {
  if (authorizerInstance) return authorizerInstance
  if (initializingPromise) {
    await initializingPromise.catch(() => {})
  }
  return authorizerInstance
}

/**
 * Safely initialize the Authorizer with feature flag and validation checks.
 * This function is idempotent - safe to call multiple times.
 * Errors are logged but don't block the calling flow.
 * @param user - The authenticated user
 * @param brandSession - The brand session to initialize the authorizer for
 * @param context - Context string for error logging
 * @param sessionProvider
 * @param getCsrfToken - Lazy reader for the CSRF token. Required when
 *   sessionProvider === 'auth-service' so the cookie provider can attach
 *   X-CSRFToken on each request.
 */
export const safeInitializeAuthorizer = async (
  user: User,
  brandSession: BrandSession,
  context: string,
  sessionProvider?: 'olympus' | 'auth-service',
  getCsrfToken?: () => string | null | undefined
): Promise<void> => {
  if (getAuthorizer()) {
    return
  }

  if (initializingPromise) {
    await initializingPromise.catch(() => {})
    return
  }

  const thisPromise = initializeAuthorizer(
    user,
    brandSession,
    sessionProvider,
    getCsrfToken
  )
  initializingPromise = thisPromise
  try {
    await thisPromise
  } catch (error) {
    // Surface EVERY authorizer-init failure, not just the token-expired one
    // that forces a logout. Non-expired auth-service failures here are
    // otherwise swallowed (logged at error only) — that silent path is the
    // blind spot that made logout reports hard to debug.
    if (error instanceof AuthenticationError) {
      logInfo('auth.authorizer_init_failed', {
        context,
        errorCode: error.errorCode,
        sessionProvider: sessionProvider ?? null,
        statusCode: error.statusCode,
      })
    }

    // The auth-service provider bypasses OlympusClient's 401 interceptor, so an
    // expired session would otherwise be swallowed, stranding the user with a
    // null Authorizer. Route it through the same `auth.logout` path instead.
    if (
      sessionProvider === 'auth-service' &&
      error instanceof AuthenticationError &&
      error.errorCode === AuthErrorCodes.TokenExpired
    ) {
      useEmitter.emit('auth.logout', {
        context,
        errorCode: error.errorCode,
        source: 'authorizer-token-expired',
        statusCode: error.statusCode,
      })
      return
    }

    // Same stale shared-cookie CSRF 403 the OlympusClient interceptor catches,
    // but on the auth-service path that bypasses it: emit the recovery event so
    // useSessionManager does the single logout, instead of letting the
    // authorizer keep storming auth-service. Unlike token-expired (an expected
    // outcome that early-returns to stay quiet), a stale CSRF is the bug this
    // works around — fall through to logError so it stays visible even when the
    // recovery flag is off and the handler no-ops.
    if (
      sessionProvider === 'auth-service' &&
      error instanceof AuthenticationError &&
      error.errorCode === AuthErrorCodes.CsrfInvalid
    ) {
      useEmitter.emit('auth.csrf-invalid', {
        context,
        errorCode: error.errorCode,
        source: 'csrf-invalid',
        statusCode: error.statusCode,
      })
      logError(error, {
        context: `${context} - Failed to initialize Authorizer`,
      })
      return
    }

    // Any other terminal 401/403 (generic 401, or a 403 that isn't the CSRF
    // code). A failed init never sets the authorizer, so every guard and reload
    // re-fires it and re-hits my-access/my-permissions — 401/403 aren't
    // recoverable by retrying, so that storms auth-service indefinitely. End the
    // session on /login instead. 5xx/network stay a plain logError so a
    // transient blip doesn't sign the user out.
    if (
      sessionProvider === 'auth-service' &&
      error instanceof AuthenticationError &&
      (error.statusCode === 401 || error.statusCode === 403)
    ) {
      useEmitter.emit('auth.logout', {
        context,
        errorCode: error.errorCode,
        source: 'authorizer-terminal-auth-error',
        statusCode: error.statusCode,
      })
      logError(error, {
        context: `${context} - Failed to initialize Authorizer`,
      })
      return
    }

    logError(error, { context: `${context} - Failed to initialize Authorizer` })
  } finally {
    if (initializingPromise === thisPromise) {
      initializingPromise = null
    }
  }
}
